<template>
  <section class="register-group-wrapper">
    <!-- Header de Navegação e Título da Página -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-1">
      <div>
        <div class="d-flex align-items-center mb-50">
          <b-button
            variant="flat-secondary"
            size="sm"
            class="btn-icon mr-50"
            :to="{ name: 'list-group' }"
            v-b-tooltip.hover
            :title="$t('Voltar aos Grupos')"
          >
            <feather-icon icon="ArrowLeftIcon" size="18" />
          </b-button>
          <h3 class="font-weight-bolder text-primary mb-0 d-flex align-items-center">
            <feather-icon icon="LayersIcon" size="22" class="mr-50" />
            {{ groupData.id ? $t('Editar Grupo') : $t('Novo Grupo de Ativos') }}
          </h3>
          <b-badge v-if="groupData.id" variant="light-primary" pill class="ml-1 font-small-2">
            {{ totalAssets }} {{ totalAssets === 1 ? $t('ativo') : $t('ativos') }}
          </b-badge>
        </div>
        <p class="text-muted font-small-3 mb-0 ml-md-3">
          {{ groupData.id
            ? $t('Gerencie os veículos vinculados e configure os limites de alarmes para este grupo.')
            : $t('Defina o nome e a descrição do grupo para começar a organizar sua frota.')
          }}
        </p>
      </div>

      <div class="d-flex align-items-center mt-1 mt-md-0" style="gap: 8px;">
        <b-button
          variant="outline-secondary"
          size="sm"
          :to="{ name: 'list-group' }"
          class="d-inline-flex align-items-center"
        >
          <feather-icon icon="ListIcon" size="14" class="mr-50" />
          <span>{{ $t('Listar Grupos') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Alert Informativo quando for Novo Grupo -->
    <b-alert v-if="!groupData.id" v-model="showCreationBanner" variant="light-info" dismissible class="mb-1 border-info shadow-xs">
      <div class="d-flex align-items-center py-25">
        <b-avatar variant="light-info" size="38px" class="mr-1">
          <feather-icon icon="InfoIcon" size="18" class="text-info" />
        </b-avatar>
        <div>
          <h6 class="mb-25 font-weight-bolder text-info">{{ $t('Próximo Passo') }}</h6>
          <span class="font-small-3 text-muted">
            {{ $t('Após salvar este grupo, a área de vínculo de ativos, os e-mails de alerta e o painel de configuração de alarmes serão desbloqueados.') }}
          </span>
        </div>
      </div>
    </b-alert>

    <b-row>
      <!-- Coluna Esquerda: Formulário do Grupo e Lista de Ativos -->
      <b-col cols="12" :md="groupData.id ? 7 : 12" :xl="groupData.id ? 7 : 12">
        <!-- Card 1: Informações do Grupo -->
        <b-card no-body class="mb-1 shadow-sm border-0">
          <b-card-header class="border-bottom py-1 px-1">
            <div class="d-flex align-items-center">
              <feather-icon icon="FileTextIcon" size="16" class="text-primary mr-50" />
              <h5 class="mb-0 font-weight-bolder">{{ $t('Informações do Grupo') }}</h5>
            </div>
          </b-card-header>

          <b-card-body class="p-1">
            <validation-observer #default="{ handleSubmit }" ref="simpleRules">
              <b-form @submit.prevent="handleSubmit(validationForm)">
                <b-row>
                  <!-- Nome do Grupo -->
                  <b-col cols="12" md="5" class="mb-1">
                    <b-form-group :label="$t('Nome do Grupo')" label-for="name">
                      <validation-provider #default="{ errors }" name="name" rules="required">
                        <b-input-group size="sm">
                          <b-input-group-prepend is-text>
                            <feather-icon icon="LayersIcon" size="14" />
                          </b-input-group-prepend>
                          <b-form-input
                            id="name"
                            v-model="groupData.name"
                            :placeholder="$t('Ex: Frota Pesada, Operação Sul...')"
                          />
                        </b-input-group>
                        <small class="text-danger font-small-2">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <!-- Descrição do Grupo -->
                  <b-col cols="12" md="7" class="mb-1">
                    <b-form-group :label="$t('Descrição')" label-for="description">
                      <validation-provider #default="{ errors }" name="description" rules="">
                        <b-input-group size="sm">
                          <b-input-group-prepend is-text>
                            <feather-icon icon="AlignLeftIcon" size="14" />
                          </b-input-group-prepend>
                          <b-form-input
                            id="description"
                            v-model="groupData.description"
                            :placeholder="$t('Ex: Caminhões que operam na rota Curitiba...')"
                          />
                        </b-input-group>
                        <small class="text-danger font-small-2">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Seção: E-mails de Alerta do Grupo (apenas quando o grupo já existir) -->
                <div v-if="groupData.id" class="alert-emails-section mb-1">
                  <div class="d-flex flex-wrap align-items-center justify-content-between mb-50">
                    <div class="d-flex align-items-center">
                      <feather-icon icon="MailIcon" size="15" class="text-primary mr-50" />
                      <label for="alert-email-input" class="mb-0 font-weight-bolder font-small-3 text-body">
                        {{ $t('E-mails de Alerta') }}
                      </label>
                      <b-badge
                        :variant="alertEmailsList.length >= MAX_ALERT_EMAILS ? 'light-warning' : (alertEmailsList.length > 0 ? 'light-primary' : 'light-secondary')"
                        pill
                        class="ml-50 font-small-1"
                      >
                        {{ alertEmailsList.length }}/{{ MAX_ALERT_EMAILS }}
                      </b-badge>
                    </div>

                    <b-link
                      v-if="alertEmailsList.length > 1"
                      class="font-small-2 text-danger font-weight-bold d-inline-flex align-items-center"
                      @click="clearAlertEmails"
                    >
                      <feather-icon icon="Trash2Icon" size="12" class="mr-25" />
                      <span>{{ $t('Limpar todos') }}</span>
                    </b-link>
                  </div>

                  <!-- Aviso informativo sobre salvar alterações -->
                  <div class="alert-emails-rule-banner d-flex align-items-center mb-75">
                    <feather-icon icon="InfoIcon" size="14" class="text-primary mr-50 flex-shrink-0" />
                    <span class="font-small-2 text-muted">
                      {{ $t('Para que os e-mails fiquem devidamente vinculados a este grupo, lembre-se de clicar em') }}
                      <strong class="text-primary font-weight-bolder">{{ $t('Salvar Alterações') }}</strong>.
                    </span>
                  </div>

                  <!-- Campo de entrada de e-mail(s) -->
                  <b-input-group size="sm" class="alert-email-input-group">
                    <b-input-group-prepend is-text>
                      <feather-icon icon="AtSignIcon" size="14" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="alert-email-input"
                      v-model="alertEmailInput"
                      type="text"
                      autocomplete="off"
                      :state="alertEmailError ? false : null"
                      :placeholder="isAlertEmailLimitReached
                        ? $t('Limite máximo de 5 e-mails atingido')
                        : $t('Digite ou cole e-mail(s) (ex: alertas.sul@empresa.com) e pressione Enter...')"
                      @keydown="onAlertEmailKeydown"
                      @paste="onAlertEmailPaste"
                      @input="onAlertEmailInput"
                    />
                    <b-input-group-append>
                      <b-button
                        id="btn-add-alert-email"
                        variant="outline-primary"
                        size="sm"
                        type="button"
                        class="d-inline-flex align-items-center"
                        @click="addAlertEmail()"
                      >
                        <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                        <span>{{ $t('Adicionar') }}</span>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>

                  <!-- Mensagens de erro ou feedback -->
                  <small
                    v-if="alertEmailError"
                    id="alert-email-error"
                    class="text-danger font-small-2 font-weight-bold d-flex align-items-center mt-50"
                  >
                    <feather-icon icon="AlertCircleIcon" size="13" class="mr-25 flex-shrink-0" />
                    <span>{{ alertEmailError }}</span>
                  </small>
                  <small
                    v-else-if="alertEmailDuplicateNotice"
                    class="text-info font-small-2 d-flex align-items-center mt-50"
                  >
                    <feather-icon icon="CheckCircleIcon" size="13" class="mr-25 flex-shrink-0" />
                    <span>{{ alertEmailDuplicateNotice }}</span>
                  </small>
                  <small v-else class="text-muted font-small-1 d-block mt-25">
                    {{ $t('Pressione Enter, vírgula ou ponto e vírgula para adicionar. Máximo de 5 e-mails válidos (até 254 caracteres cada).') }}
                  </small>

                  <!-- Lista de Chips de E-mails Cadastrados -->
                  <div v-if="alertEmailsList.length > 0" class="alert-emails-chips mt-75">
                    <span
                      v-for="(emailItem, index) in alertEmailsList"
                      :key="emailItem.toLowerCase() + '-' + index"
                      class="alert-email-chip"
                    >
                      <feather-icon icon="MailIcon" size="12" class="text-primary mr-50 flex-shrink-0" />
                      <span class="alert-email-text">{{ emailItem }}</span>
                      <button
                        type="button"
                        class="alert-email-remove"
                        v-b-tooltip.hover
                        :title="$t('Remover e-mail')"
                        @click="removeAlertEmail(index)"
                      >
                        <feather-icon icon="XIcon" size="12" />
                      </button>
                    </span>
                  </div>

                  <!-- Empty State quando nenhum e-mail foi adicionado -->
                  <div v-else class="alert-emails-empty mt-75 d-flex align-items-center">
                    <feather-icon icon="InboxIcon" size="14" class="text-muted mr-50 flex-shrink-0" />
                    <span class="font-small-2 text-muted">
                      {{ $t('Nenhum e-mail de alerta configurado para este grupo.') }}
                    </span>
                  </div>
                </div>

                <div class="d-flex justify-content-end">
                  <b-button
                    variant="primary"
                    size="sm"
                    type="submit"
                    :disabled="isSavingGroup"
                    class="d-inline-flex align-items-center"
                  >
                    <b-spinner v-if="isSavingGroup" small class="mr-50" />
                    <feather-icon v-else icon="SaveIcon" size="14" class="mr-50" />
                    <span>{{ groupData.id ? $t('Salvar Alterações') : $t('Criar Grupo') }}</span>
                  </b-button>
                </div>
              </b-form>
            </validation-observer>
          </b-card-body>
        </b-card>

        <!-- Card 2: Ativos Vinculados ao Grupo -->
        <b-card v-if="groupData.id" no-body class="mb-1 shadow-sm border-0">
          <b-card-header class="border-bottom py-1 px-1 d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <feather-icon icon="TruckIcon" size="16" class="text-primary mr-50" />
              <h5 class="mb-0 font-weight-bolder">{{ $t('Ativos no Grupo') }}</h5>
              <b-badge variant="light-primary" pill class="ml-50 font-small-2">
                {{ totalAssets }}
              </b-badge>
            </div>

            <b-button
              variant="primary"
              size="sm"
              @click="openModalBind"
              class="d-inline-flex align-items-center"
            >
              <feather-icon icon="PlusIcon" size="14" class="mr-25" />
              <span>{{ $t('Adicionar Ativo') }}</span>
            </b-button>
          </b-card-header>

          <!-- Tabela de Ativos Vinculados -->
          <div v-if="totalAssets > 0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th scope="col" class="py-75">{{ $t('Identificador') }}</th>
                    <th scope="col" class="py-75">{{ $t('Apelido') }}</th>
                    <th scope="col" class="py-75">{{ $t('Veículo / Modelo') }}</th>
                    <th scope="col" class="py-75 text-right">{{ $t('Ações') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="assetItem in assets" :key="assetItem.assetId">
                    <td class="align-middle">
                      <span class="asset-plate-badge font-weight-bold font-small-2">
                        {{ assetItem.plate || assetItem.chassisNumber }}
                      </span>
                    </td>
                    <td class="align-middle font-small-3 text-body">
                      {{ assetItem.nickname || '-' }}
                    </td>
                    <td class="align-middle">
                      <span class="font-small-3 font-weight-bold d-block text-body">
                        {{ assetItem.manufacturerName || '' }} {{ assetItem.modelName || '' }}
                      </span>
                      <small v-if="assetItem.modelYear" class="text-muted">
                        {{ assetItem.modelYear }}
                      </small>
                    </td>
                    <td class="align-middle text-right">
                      <b-button
                        variant="flat-danger"
                        size="sm"
                        class="btn-icon"
                        @click="modalDelete(assetItem.assetId, assetItem.bindId)"
                        v-b-tooltip.hover
                        :title="$t('Desvincular ativo deste grupo')"
                      >
                        <feather-icon icon="Trash2Icon" size="14" />
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Paginação de Ativos -->
            <div class="p-1 border-top d-flex justify-content-between align-items-center">
              <small class="text-muted">
                {{ $t('Total') }}: <strong>{{ totalAssets }}</strong> {{ $t('ativos vinculados') }}
              </small>
              <b-pagination
                v-model="currentPage"
                :total-rows="totalAssets"
                :per-page="perPage"
                first-number
                last-number
                size="sm"
                class="mb-0"
              />
            </div>
          </div>

          <!-- Empty State quando totalAssets == 0 -->
          <div v-else class="text-center py-3 px-2">
            <b-avatar variant="light-primary" size="52px" class="mb-1">
              <feather-icon icon="TruckIcon" size="26" class="text-primary" />
            </b-avatar>
            <h6 class="font-weight-bolder mb-50">{{ $t('Nenhum ativo vinculado a este grupo') }}</h6>
            <p class="text-muted font-small-3 mb-1 mx-auto" style="max-width: 420px;">
              {{ $t('Adicione veículos ou equipamentos para que as regras de alarmes configuradas à direita sejam aplicadas a eles.') }}
            </p>
            <b-button
              variant="primary"
              size="sm"
              @click="openModalBind"
              class="d-inline-flex align-items-center"
            >
              <feather-icon icon="PlusIcon" size="14" class="mr-50" />
              <span>{{ $t('Vincular Primeiro Ativo') }}</span>
            </b-button>
          </div>
        </b-card>
      </b-col>

      <!-- Coluna Direita: Painel de Configurações de Alarmes -->
      <b-col v-if="groupData.id" cols="12" md="5" xl="5">
        <b-card no-body class="mb-1 shadow-sm border-0 alarms-panel-card">
          <b-card-header class="border-bottom py-1 px-1">
            <div>
              <div class="d-flex align-items-center">
                <feather-icon icon="BellIcon" size="16" class="text-primary mr-50" />
                <h5 class="mb-0 font-weight-bolder">{{ $t('Alarmes do Grupo') }}</h5>
              </div>
              <small class="text-muted d-block mt-25">
                {{ $t('Regras ativas aplicadas à telemetria de todos os veículos do grupo.') }}
              </small>
            </div>
          </b-card-header>

          <!-- Loading Skeletons -->
          <div v-if="isLoadingAlarms" class="p-1">
            <b-row>
              <b-col cols="12" class="mb-1" v-for="n in 5" :key="'sk-alarm-' + n">
                <b-skeleton type="button" width="100%" height="68px" class="rounded" />
              </b-col>
            </b-row>
          </div>

          <!-- Alarm Cards Scrollable Grid -->
          <div v-else class="alarms-container p-1">
            <div class="alarms-grid">
              <!-- 1. Move Alarm -->
              <div class="alarm-item-card shadow-xs" :class="{ 'is-active': alarmMove.val }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <b-avatar :variant="alarmMove.val ? 'light-primary' : 'light-secondary'" size="34px" class="mr-75">
                      <feather-icon icon="MoveIcon" size="16" />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder font-small-3">{{ $t('Movimento') }}</h6>
                      <small class="text-muted">{{ $t('Início de deslocamento') }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <feather-icon v-b-tooltip.hover class="text-muted mr-75 cursor-pointer" :title="titleTootipMove" icon="HelpCircleIcon" size="14" />
                    <b-form-checkbox id="check-move" v-model="alarmMove.val" @change="toogleAlarm(MOVE_ALARM_NAME)" name="alarm-move" switch inline class="mr-0" />
                  </div>
                </div>
              </div>

              <!-- 2. Weekend Move Alarm -->
              <div class="alarm-item-card shadow-xs" :class="{ 'is-active': alarmWeekendMove.val }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <b-avatar :variant="alarmWeekendMove.val ? 'light-primary' : 'light-secondary'" size="34px" class="mr-75">
                      <feather-icon icon="CalendarIcon" size="16" />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder font-small-3">{{ $t('Movimento no Fim de Semana') }}</h6>
                      <small class="text-muted">{{ $t('Uso fora de dias úteis') }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <feather-icon v-b-tooltip.hover class="text-muted mr-75 cursor-pointer" :title="titleTootipWeekendMove" icon="HelpCircleIcon" size="14" />
                    <b-form-checkbox id="check-weekend-move" v-model="alarmWeekendMove.val" @change="toogleAlarm(WEEKEND_MOVE_ALARM_NAME)" name="alarm-weekendmove" switch inline class="mr-0" />
                  </div>
                </div>
              </div>

              <!-- 3. Ignition Alarm -->
              <div class="alarm-item-card shadow-xs" :class="{ 'is-active': alarmIgnition.val }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <b-avatar :variant="alarmIgnition.val ? 'light-success' : 'light-secondary'" size="34px" class="mr-75">
                      <feather-icon icon="KeyIcon" size="16" />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder font-small-3">{{ $t('Ignição') }}</h6>
                      <small class="text-muted">{{ $t('Veículo ligado') }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <feather-icon v-b-tooltip.hover class="text-muted mr-75 cursor-pointer" :title="titleTootipIgnition" icon="HelpCircleIcon" size="14" />
                    <b-form-checkbox id="check-ignition" v-model="alarmIgnition.val" @change="toogleAlarm(IGNITION_ALARM_NAME)" name="alarm-ignition" switch inline class="mr-0" />
                  </div>
                </div>
              </div>



              <!-- 6. Over Speed Alarm (with value) -->
              <div class="alarm-item-card shadow-xs" :class="{ 'is-active': alarmOverSpeed.val }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <b-avatar :variant="alarmOverSpeed.val ? 'light-danger' : 'light-secondary'" size="34px" class="mr-75">
                      <feather-icon icon="TrendingUpIcon" size="16" />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder font-small-3">{{ $t('Excesso de Velocidade') }}</h6>
                      <div v-if="alarmOverSpeed.val && alarmOverSpeed.speed" class="d-flex align-items-center mt-25">
                        <b-badge
                          variant="light-danger"
                          pill
                          class="cursor-pointer font-small-1 py-25 px-50"
                          @click="showModalOverSpeed"
                          v-b-tooltip.hover
                          :title="$t('Clique para alterar a velocidade')"
                        >
                          {{ alarmOverSpeed.speed }} km/h
                          <feather-icon icon="Edit2Icon" size="10" class="ml-25" />
                        </b-badge>
                      </div>
                      <small v-else class="text-muted">{{ $t('Limite máximo de velocidade') }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <feather-icon v-b-tooltip.hover class="text-muted mr-75 cursor-pointer" :title="titleTootipOverSpeed" icon="HelpCircleIcon" size="14" />
                    <b-form-checkbox id="check-over-speed" v-model="alarmOverSpeed.val" @change="toogleAlarm(OVER_SPEED_ALARM_NAME)" name="alarm-overspeed" switch inline class="mr-0" />
                  </div>
                </div>
              </div>

              <!-- 7. High Fuel Consumption (with value) -->
              <div class="alarm-item-card shadow-xs" :class="{ 'is-active': alarmHighFuelConsumption.val }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <b-avatar :variant="alarmHighFuelConsumption.val ? 'light-warning' : 'light-secondary'" size="34px" class="mr-75">
                      <feather-icon icon="TrendingUpIcon" size="16" />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder font-small-3">{{ $t('Alto Consumo') }}</h6>
                      <div v-if="alarmHighFuelConsumption.val && alarmHighFuelConsumption.consumption" class="d-flex align-items-center mt-25">
                        <b-badge
                          variant="light-warning"
                          pill
                          class="cursor-pointer font-small-1 py-25 px-50"
                          @click="showModalHighFuelConsumption"
                          v-b-tooltip.hover
                          :title="$t('Clique para alterar o consumo')"
                        >
                          {{ alarmHighFuelConsumption.consumption }} L/h
                          <feather-icon icon="Edit2Icon" size="10" class="ml-25" />
                        </b-badge>
                      </div>
                      <small v-else class="text-muted">{{ $t('Consumo de combustível excessivo') }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <feather-icon v-b-tooltip.hover class="text-muted mr-75 cursor-pointer" :title="titleTootipHighFuelConsumption" icon="HelpCircleIcon" size="14" />
                    <b-form-checkbox id="check-high-fuel-consumption" v-model="alarmHighFuelConsumption.val" @change="toogleAlarm(HIGH_FUEL_CONSUMPTION_ALARM_NAME)" name="alarm-highfuelconsumption" switch inline class="mr-0" />
                  </div>
                </div>
              </div>

              <!-- 8. Low Fuel (with value) -->
              <div class="alarm-item-card shadow-xs" :class="{ 'is-active': alarmLowFuel.val }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <b-avatar :variant="alarmLowFuel.val ? 'light-warning' : 'light-secondary'" size="34px" class="mr-75">
                      <feather-icon icon="DropletIcon" size="16" />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder font-small-3">{{ $t('Nível Baixo de Combustível') }}</h6>
                      <div v-if="alarmLowFuel.val && alarmLowFuel.fuel" class="d-flex align-items-center mt-25">
                        <b-badge
                          variant="light-warning"
                          pill
                          class="cursor-pointer font-small-1 py-25 px-50"
                          @click="showModalLowFuel"
                          v-b-tooltip.hover
                          :title="$t('Clique para alterar o nível mínimo')"
                        >
                          {{ alarmLowFuel.fuel }} %
                          <feather-icon icon="Edit2Icon" size="10" class="ml-25" />
                        </b-badge>
                      </div>
                      <small v-else class="text-muted">{{ $t('Tanque abaixo do limiar') }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <feather-icon v-b-tooltip.hover class="text-muted mr-75 cursor-pointer" :title="titleTootipLowFuel" icon="HelpCircleIcon" size="14" />
                    <b-form-checkbox id="check-low-fuel" v-model="alarmLowFuel.val" @change="toogleAlarm(LOW_FUEL_ALARM_NAME)" name="alarm-lowfuel" switch inline class="mr-0" />
                  </div>
                </div>
              </div>

              <!-- 9. Long Time Without Connection (with value) -->
              <div class="alarm-item-card shadow-xs" :class="{ 'is-active': alarmLongTime.val }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <b-avatar :variant="alarmLongTime.val ? 'light-secondary' : 'light-secondary'" size="34px" class="mr-75">
                      <feather-icon icon="WifiOffIcon" size="16" />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder font-small-3">{{ $t('Sem Conexão Prolongada') }}</h6>
                      <div v-if="alarmLongTime.val && alarmLongTime.longTime" class="d-flex align-items-center mt-25">
                        <b-badge
                          variant="light-secondary"
                          pill
                          class="cursor-pointer font-small-1 py-25 px-50"
                          @click="showModalLongTime"
                          v-b-tooltip.hover
                          :title="$t('Clique para alterar as horas')"
                        >
                          {{ alarmLongTime.longTime }} {{ $t('horas') }}
                          <feather-icon icon="Edit2Icon" size="10" class="ml-25" />
                        </b-badge>
                      </div>
                      <small v-else class="text-muted">{{ $t('Tempo sem comunicação') }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <feather-icon v-b-tooltip.hover class="text-muted mr-75 cursor-pointer" :title="titleTootipLongTime" icon="HelpCircleIcon" size="14" />
                    <b-form-checkbox id="check-long-time" v-model="alarmLongTime.val" @change="toogleAlarm(LONG_TIME_ALARM_NAME)" name="alarm-longtime" switch inline class="mr-0" />
                  </div>
                </div>
              </div>

              <!-- 10. Engine Coolant Temperature (with value) -->
              <div class="alarm-item-card shadow-xs" :class="{ 'is-active': alarmEngineCoolantTemperature.val }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <b-avatar :variant="alarmEngineCoolantTemperature.val ? 'light-danger' : 'light-secondary'" size="34px" class="mr-75">
                      <feather-icon icon="ThermometerIcon" size="16" />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder font-small-3">{{ $t('Temperatura do Arrefecimento') }}</h6>
                      <div v-if="alarmEngineCoolantTemperature.val && alarmEngineCoolantTemperature.temperature" class="d-flex align-items-center mt-25">
                        <b-badge
                          variant="light-danger"
                          pill
                          class="cursor-pointer font-small-1 py-25 px-50"
                          @click="showModalEngineCoolantTemperature"
                          v-b-tooltip.hover
                          :title="$t('Clique para alterar a temperatura')"
                        >
                          {{ alarmEngineCoolantTemperature.temperature }} ºC
                          <feather-icon icon="Edit2Icon" size="10" class="ml-25" />
                        </b-badge>
                      </div>
                      <small v-else class="text-muted">{{ $t('Líquido de arrefecimento do motor') }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <feather-icon v-b-tooltip.hover class="text-muted mr-75 cursor-pointer" :title="titleTootipTemperature" icon="HelpCircleIcon" size="14" />
                    <b-form-checkbox id="check-engine-temperature" v-model="alarmEngineCoolantTemperature.val" @change="toogleAlarm(ENGINE_COOLANT_TEMPERATURE_ALARM_NAME)" name="alarm-engine-coolant-temperature" switch inline class="mr-0" />
                  </div>
                </div>
              </div>

              <!-- 11. Hydraulic Oil Temperature (with value) -->
              <div class="alarm-item-card shadow-xs" :class="{ 'is-active': alarmHydraulicTemperature.val }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <b-avatar :variant="alarmHydraulicTemperature.val ? 'light-danger' : 'light-secondary'" size="34px" class="mr-75">
                      <feather-icon icon="ThermometerIcon" size="16" />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder font-small-3">{{ $t('Temperatura do Óleo Hidráulico') }}</h6>
                      <div v-if="alarmHydraulicTemperature.val && alarmHydraulicTemperature.temperature" class="d-flex align-items-center mt-25">
                        <b-badge
                          variant="light-danger"
                          pill
                          class="cursor-pointer font-small-1 py-25 px-50"
                          @click="showModalHydraulicOilTemperature"
                          v-b-tooltip.hover
                          :title="$t('Clique para alterar a temperatura')"
                        >
                          {{ alarmHydraulicTemperature.temperature }} ºC
                          <feather-icon icon="Edit2Icon" size="10" class="ml-25" />
                        </b-badge>
                      </div>
                      <small v-else class="text-muted">{{ $t('Circuito hidráulico') }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <feather-icon v-b-tooltip.hover class="text-muted mr-75 cursor-pointer" :title="titleTootipTemperature" icon="HelpCircleIcon" size="14" />
                    <b-form-checkbox id="check-hydraulic-temperature" v-model="alarmHydraulicTemperature.val" @change="toogleAlarm(HYDRAULIC_OIL_TEMPERATURE_ALARM_NAME)" name="alarm-hydraulic-temperature" switch inline class="mr-0" />
                  </div>
                </div>
              </div>

              <!-- 12. Transmission Oil Temperature (with value) -->
              <div class="alarm-item-card shadow-xs" :class="{ 'is-active': alarmTransmissionTemperature.val }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <b-avatar :variant="alarmTransmissionTemperature.val ? 'light-danger' : 'light-secondary'" size="34px" class="mr-75">
                      <feather-icon icon="ThermometerIcon" size="16" />
                    </b-avatar>
                    <div>
                      <h6 class="mb-0 font-weight-bolder font-small-3">{{ $t('Temperatura da Transmissão') }}</h6>
                      <div v-if="alarmTransmissionTemperature.val && alarmTransmissionTemperature.temperature" class="d-flex align-items-center mt-25">
                        <b-badge
                          variant="light-danger"
                          pill
                          class="cursor-pointer font-small-1 py-25 px-50"
                          @click="showModalTransmissionOilTemperature"
                          v-b-tooltip.hover
                          :title="$t('Clique para alterar a temperatura')"
                        >
                          {{ alarmTransmissionTemperature.temperature }} ºC
                          <feather-icon icon="Edit2Icon" size="10" class="ml-25" />
                        </b-badge>
                      </div>
                      <small v-else class="text-muted">{{ $t('Óleo da caixa de câmbio') }}</small>
                    </div>
                  </div>
                  <div class="d-flex align-items-center">
                    <feather-icon v-b-tooltip.hover class="text-muted mr-75 cursor-pointer" :title="titleTootipTemperature" icon="HelpCircleIcon" size="14" />
                    <b-form-checkbox id="check-transmission" v-model="alarmTransmissionTemperature.val" @change="toogleAlarm(TRANSMISSION_OIL_TEMPERATURE_ALARM_NAME)" name="alarm-transmission-temperature" switch inline class="mr-0" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Modal: Adicionar Ativo ao Grupo -->
    <b-modal
      v-model="showModal"
      no-close-on-backdrop
      id="modal-cadastro"
      size="md"
      centered
      :title="$t('Vincular Ativo ao Grupo')"
      title-class="font-weight-bolder text-primary"
      hide-footer
      @hide="limparFormCadastro"
    >
      <div class="mb-1">
        <span class="text-muted font-small-2 d-block">{{ $t('Grupo Alvo') }}:</span>
        <b-badge variant="light-primary" class="font-small-2 py-50 px-75">
          <feather-icon icon="LayersIcon" size="12" class="mr-25" />
          {{ groupData.name }}
        </b-badge>
      </div>

      <p class="font-small-3 text-muted mb-1">
        {{ $t('Digite ao menos 3 caracteres da placa ou chassi para buscar os veículos disponíveis.') }}
      </p>

      <b-form-group :label="$t('Buscar Veículo / Ativo')" label-for="asset-field">
        <vue-autosuggest
          v-model="asset.name"
          :suggestions="filteredOptions"
          :input-props="inputProps"
          :get-suggestion-value="getSuggestionValue"
          @selected="selectHandler"
          @input="onInputChange"
        >
          <template slot-scope="{ suggestion }">
            <div class="p-50 border-bottom d-flex align-items-center justify-content-between">
              <div>
                <span class="font-weight-bold d-block">
                  <feather-icon icon="TruckIcon" size="14" class="mr-25 text-primary" />
                  {{ suggestion.item.plate || suggestion.item.chassisNumber }}
                </span>
                <small v-if="suggestion.item.nickname" class="text-muted">
                  {{ suggestion.item.nickname }}
                </small>
              </div>
              <small v-if="suggestion.item.modelName" class="text-muted">
                {{ suggestion.item.manufacturerName }} {{ suggestion.item.modelName }}
              </small>
            </div>
          </template>
        </vue-autosuggest>
      </b-form-group>

      <div v-if="asset.id" class="alert alert-light-success p-75 mb-1 d-flex align-items-center">
        <feather-icon icon="CheckCircleIcon" size="16" class="text-success mr-50" />
        <span class="font-small-3 font-weight-bold">{{ $t('Selecionado') }}: {{ asset.name }}</span>
      </div>

      <div class="d-flex justify-content-end mt-2" style="gap: 8px;">
        <b-button variant="outline-secondary" size="sm" @click="showModal = false">
          {{ $t('Cancelar') }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="!asset.id || isBindingAsset"
          @click="bindGroup"
          class="d-inline-flex align-items-center"
        >
          <b-spinner v-if="isBindingAsset" small class="mr-50" />
          <feather-icon v-else icon="PlusIcon" size="14" class="mr-50" />
          <span>{{ isBindingAsset ? $t('Adicionando...') : $t('Confirmar Vínculo') }}</span>
        </b-button>
      </div>
    </b-modal>

    <!-- Modal: Limiar Excesso de Velocidade -->
    <b-modal
      ref="modal-create-over-speed"
      size="sm"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="p-1 text-center">
        <b-avatar variant="light-danger" size="48px" class="mb-1">
          <feather-icon icon="TrendingUpIcon" size="24" class="text-danger" />
        </b-avatar>
        <h5 class="font-weight-bolder mb-50">{{ $t('Excesso de Velocidade') }}</h5>
        <p class="font-small-3 text-muted mb-1">{{ $t('Defina a velocidade máxima permitida em km/h.') }}</p>
        <b-input-group append="km/h" class="mb-1">
          <b-form-input
            v-model="maxSpeed"
            type="number"
            placeholder="Ex: 80"
            @keyup.enter="validateOverSpeed"
            autofocus
          />
        </b-input-group>
        <div class="d-flex justify-content-between mt-1">
          <b-button variant="outline-secondary" size="sm" @click="cancelOverSpeed">{{ $t('Cancelar') }}</b-button>
          <b-button variant="primary" size="sm" @click="validateOverSpeed">{{ $t('Salvar Limiar') }}</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal: Limiar Alto Consumo de Combustível -->
    <b-modal
      ref="modal-create-high-fuel-consumption"
      size="sm"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="p-1 text-center">
        <b-avatar variant="light-warning" size="48px" class="mb-1">
          <feather-icon icon="TrendingUpIcon" size="24" class="text-warning" />
        </b-avatar>
        <h5 class="font-weight-bolder mb-50">{{ $t('Alto Consumo') }}</h5>
        <p class="font-small-3 text-muted mb-1">{{ $t('Defina o consumo máximo aceitável em Litros/Hora.') }}</p>
        <b-input-group append="L/h" class="mb-1">
          <b-form-input
            v-model="consumption"
            type="number"
            placeholder="Ex: 35"
            @keyup.enter="validateHighFuelConsumption"
            autofocus
          />
        </b-input-group>
        <div class="d-flex justify-content-between mt-1">
          <b-button variant="outline-secondary" size="sm" @click="cancelHighFuelConsumption">{{ $t('Cancelar') }}</b-button>
          <b-button variant="primary" size="sm" @click="validateHighFuelConsumption">{{ $t('Salvar Limiar') }}</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal: Limiar Nível Baixo de Combustível -->
    <b-modal
      ref="modal-create-low-fuel"
      size="sm"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="p-1 text-center">
        <b-avatar variant="light-warning" size="48px" class="mb-1">
          <feather-icon icon="DropletIcon" size="24" class="text-warning" />
        </b-avatar>
        <h5 class="font-weight-bolder mb-50">{{ $t('Nível Baixo de Combustível') }}</h5>
        <p class="font-small-3 text-muted mb-1">{{ $t('Defina a porcentagem mínima de combustível.') }}</p>
        <b-input-group append="%" class="mb-1">
          <b-form-input
            v-model="fuel"
            type="number"
            placeholder="Ex: 15"
            @keyup.enter="validateLowFuel"
            autofocus
          />
        </b-input-group>
        <div class="d-flex justify-content-between mt-1">
          <b-button variant="outline-secondary" size="sm" @click="cancelLowFuel">{{ $t('Cancelar') }}</b-button>
          <b-button variant="primary" size="sm" @click="validateLowFuel">{{ $t('Salvar Limiar') }}</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal: Limiar Tempo Sem Conexão -->
    <b-modal
      ref="modal-create-long-time"
      size="sm"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="p-1 text-center">
        <b-avatar variant="light-secondary" size="48px" class="mb-1">
          <feather-icon icon="WifiOffIcon" size="24" class="text-muted" />
        </b-avatar>
        <h5 class="font-weight-bolder mb-50">{{ $t('Tempo Sem Conexão') }}</h5>
        <p class="font-small-3 text-muted mb-1">{{ $t('Defina o limite em horas sem envio de telemetria.') }}</p>
        <b-input-group append="horas" class="mb-1">
          <b-form-input
            v-model="longTime"
            type="number"
            placeholder="Ex: 24"
            @keyup.enter="validateLongTime"
            autofocus
          />
        </b-input-group>
        <div class="d-flex justify-content-between mt-1">
          <b-button variant="outline-secondary" size="sm" @click="cancelLongTime">{{ $t('Cancelar') }}</b-button>
          <b-button variant="primary" size="sm" @click="validateLongTime">{{ $t('Salvar Limiar') }}</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal: Limiar Temp Arrefecimento -->
    <b-modal
      ref="modal-create-engine-coolant-temperature"
      size="sm"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="p-1 text-center">
        <b-avatar variant="light-danger" size="48px" class="mb-1">
          <feather-icon icon="ThermometerIcon" size="24" class="text-danger" />
        </b-avatar>
        <h5 class="font-weight-bolder mb-50">{{ $t('Temp. do Arrefecimento') }}</h5>
        <p class="font-small-3 text-muted mb-1">{{ $t('Defina a temperatura máxima do motor em ºC.') }}</p>
        <b-input-group append="ºC" class="mb-1">
          <b-form-input
            v-model="coolantTemperature"
            type="number"
            placeholder="Ex: 100"
            @keyup.enter="validateEngineCoolantTemperature"
            autofocus
          />
        </b-input-group>
        <div class="d-flex justify-content-between mt-1">
          <b-button variant="outline-secondary" size="sm" @click="cancelEngineCoolantTemperature">{{ $t('Cancelar') }}</b-button>
          <b-button variant="primary" size="sm" @click="validateEngineCoolantTemperature">{{ $t('Salvar Limiar') }}</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal: Limiar Temp Óleo Hidráulico -->
    <b-modal
      ref="modal-create-hydraulic-oil-temperature"
      size="sm"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="p-1 text-center">
        <b-avatar variant="light-danger" size="48px" class="mb-1">
          <feather-icon icon="ThermometerIcon" size="24" class="text-danger" />
        </b-avatar>
        <h5 class="font-weight-bolder mb-50">{{ $t('Temp. do Óleo Hidráulico') }}</h5>
        <p class="font-small-3 text-muted mb-1">{{ $t('Defina a temperatura máxima do óleo hidráulico em ºC.') }}</p>
        <b-input-group append="ºC" class="mb-1">
          <b-form-input
            v-model="hydraulicTemperature"
            type="number"
            placeholder="Ex: 85"
            @keyup.enter="validateHydraulicOilTemperature"
            autofocus
          />
        </b-input-group>
        <div class="d-flex justify-content-between mt-1">
          <b-button variant="outline-secondary" size="sm" @click="cancelHydraulicOilTemperature">{{ $t('Cancelar') }}</b-button>
          <b-button variant="primary" size="sm" @click="validateHydraulicOilTemperature">{{ $t('Salvar Limiar') }}</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal: Limiar Temp Óleo Transmissão -->
    <b-modal
      ref="modal-create-transmission-oil-temperature"
      size="sm"
      centered
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="p-1 text-center">
        <b-avatar variant="light-danger" size="48px" class="mb-1">
          <feather-icon icon="ThermometerIcon" size="24" class="text-danger" />
        </b-avatar>
        <h5 class="font-weight-bolder mb-50">{{ $t('Temp. da Transmissão') }}</h5>
        <p class="font-small-3 text-muted mb-1">{{ $t('Defina a temperatura máxima da transmissão em ºC.') }}</p>
        <b-input-group append="ºC" class="mb-1">
          <b-form-input
            v-model="transmissionTemperature"
            type="number"
            placeholder="Ex: 95"
            @keyup.enter="validateTransmissionOilTemperature"
            autofocus
          />
        </b-input-group>
        <div class="d-flex justify-content-between mt-1">
          <b-button variant="outline-secondary" size="sm" @click="cancelTransmissionOilTemperature">{{ $t('Cancelar') }}</b-button>
          <b-button variant="primary" size="sm" @click="validateTransmissionOilTemperature">{{ $t('Salvar Limiar') }}</b-button>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BAlert,
  BLink,
  BFormCheckbox,
  BModal,
  VBToggle,
  VBTooltip,
  BFormInput,
  BFormGroup,
  BForm,
  BRow,
  BCol,
  BButton,
  BAvatar,
  BPagination,
  BSkeleton,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BSpinner,
  BBadge,
} from 'bootstrap-vue'
import { ref, onUnmounted, onMounted, computed, watch } from '@vue/composition-api'
import router from '@/router'
import store from '@/store'
import managementStoreModule from '../managementStoreModule'
import { VueAutosuggest } from 'vue-autosuggest'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import alarmsStoreModule from "@/views/main/alarms/alarmsStoreModule"

export default {
  directives: { 'b-toggle': VBToggle, 'b-tooltip': VBTooltip },
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BAlert,
    BLink,
    BFormCheckbox,
    BModal,
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    BAvatar,
    BPagination,
    BSkeleton,
    BInputGroup,
    BInputGroupPrepend,
  BInputGroupAppend,
    BSpinner,
    BBadge,
    VueAutosuggest,
    ValidationProvider,
    ValidationObserver,
  },
  setup(props, context) {
    const { root } = context
    const simpleRules = ref(null)
    const groupId = router.currentRoute.params.id
    const MANAGEMENT_APP_STORE_MODULE_NAME = 'app-management'
    const ALARMS_APP_STORE_MODULE_NAME = 'app-alarms'

    // Register modules
    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)
    if (!store.hasModule(ALARMS_APP_STORE_MODULE_NAME)) store.registerModule(ALARMS_APP_STORE_MODULE_NAME, alarmsStoreModule)
    onUnmounted(() => {
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
      if (store.hasModule(ALARMS_APP_STORE_MODULE_NAME)) store.unregisterModule(ALARMS_APP_STORE_MODULE_NAME)
    })

    const showCreationBanner = ref(true)
    const filteredOptions = ref([])
    const isSavingGroup = ref(false)
    const isBindingAsset = ref(false)

    const MAX_ALERT_EMAILS = 5
    const MAX_ALERT_EMAIL_LENGTH = 254
    const groupData = ref({ id: '', name: '', description: '', alertEmails: [] })
    const alertEmailInput = ref('')
    const alertEmailError = ref('')
    const alertEmailDuplicateNotice = ref('')

    const isValidAlertEmail = (value) => {
      if (typeof value !== 'string') return false
      const trimmed = value.trim()
      if (!trimmed || trimmed.length > MAX_ALERT_EMAIL_LENGTH) return false
      if (trimmed.includes('..')) return false
      const parts = trimmed.split('@')
      if (parts.length !== 2) return false
      const [localPart, domainPart] = parts
      if (!localPart || !domainPart) return false
      if (localPart.startsWith('.') || localPart.endsWith('.')) return false
      if (domainPart.startsWith('.') || domainPart.endsWith('.')) return false
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(trimmed)
    }

    const sanitizeAndDeduplicateEmails = (rawList = []) => {
      if (!Array.isArray(rawList)) return []
      const seen = new Set()
      const result = []
      rawList.forEach((item) => {
        if (typeof item !== 'string') return
        const trimmed = item.trim()
        if (!trimmed) return
        const lower = trimmed.toLowerCase()
        if (!seen.has(lower)) {
          seen.add(lower)
          result.push(trimmed)
        }
      })
      return result
    }

    const alertEmailsList = computed(() => (Array.isArray(groupData.value.alertEmails) ? groupData.value.alertEmails : []))
    const isAlertEmailLimitReached = computed(() => alertEmailsList.value.length >= MAX_ALERT_EMAILS)

    const onAlertEmailInput = () => {
      if (alertEmailError.value) alertEmailError.value = ''
      if (alertEmailDuplicateNotice.value) alertEmailDuplicateNotice.value = ''
    }

    const addAlertEmail = (rawValue) => {
      const sourceText = typeof rawValue === 'string' ? rawValue : alertEmailInput.value
      alertEmailError.value = ''
      alertEmailDuplicateNotice.value = ''

      const rawTokens = String(sourceText || '').split(/[,;\n\r]+/)
      const candidates = rawTokens.map(token => token.trim()).filter(token => token.length > 0)

      if (candidates.length === 0) {
        alertEmailInput.value = ''
        return true
      }

      const hasInvalid = candidates.some(candidate => !isValidAlertEmail(candidate))
      if (hasInvalid) {
        alertEmailError.value = root.$t('E-mail de alerta inválido.')
        return false
      }

      const currentEmails = sanitizeAndDeduplicateEmails(groupData.value.alertEmails)
      const seen = new Set(currentEmails.map(item => item.toLowerCase()))
      const nextEmails = [...currentEmails]
      let duplicateCount = 0

      candidates.forEach((candidate) => {
        const lower = candidate.toLowerCase()
        if (seen.has(lower)) {
          duplicateCount += 1
        } else {
          seen.add(lower)
          nextEmails.push(candidate)
        }
      })

      if (nextEmails.length > MAX_ALERT_EMAILS) {
        alertEmailError.value = root.$t('O grupo aceita no máximo 5 e-mails de alerta.')
        return false
      }

      groupData.value = {
        ...groupData.value,
        alertEmails: nextEmails,
      }
      alertEmailInput.value = ''
      if (duplicateCount > 0) {
        alertEmailDuplicateNotice.value = root.$t('E-mail repetido descartado (mantido o primeiro enviado).')
      }
      return true
    }

    const removeAlertEmail = (index) => {
      const currentEmails = [...alertEmailsList.value]
      currentEmails.splice(index, 1)
      groupData.value = {
        ...groupData.value,
        alertEmails: currentEmails,
      }
      alertEmailError.value = ''
      alertEmailDuplicateNotice.value = ''
    }

    const clearAlertEmails = () => {
      groupData.value = {
        ...groupData.value,
        alertEmails: [],
      }
      alertEmailError.value = ''
      alertEmailDuplicateNotice.value = ''
    }

    const onAlertEmailKeydown = (event) => {
      if (!event) return
      if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
        event.preventDefault()
        addAlertEmail()
      }
    }

    const onAlertEmailPaste = (event) => {
      if (!event || !event.clipboardData) return
      const pastedText = event.clipboardData.getData('text')
      if (pastedText && /[,;\n\r]/.test(pastedText)) {
        event.preventDefault()
        addAlertEmail(pastedText)
      }
    }

    const validateAlertEmailsBeforeSave = () => {
      if (!groupData.value.id) return true
      if (alertEmailInput.value && alertEmailInput.value.trim().length > 0) {
        const added = addAlertEmail()
        if (!added) return false
      } else {
        alertEmailInput.value = ''
      }

      const rawEmails = Array.isArray(groupData.value.alertEmails) ? groupData.value.alertEmails : []
      const trimmedNonBlank = rawEmails
        .map(item => (typeof item === 'string' ? item.trim() : ''))
        .filter(item => item.length > 0)

      if (trimmedNonBlank.some(item => !isValidAlertEmail(item))) {
        alertEmailError.value = root.$t('E-mail de alerta inválido.')
        return false
      }

      const deduplicated = sanitizeAndDeduplicateEmails(trimmedNonBlank)
      if (deduplicated.length > MAX_ALERT_EMAILS) {
        alertEmailError.value = root.$t('O grupo aceita no máximo 5 e-mails de alerta.')
        return false
      }

      groupData.value = {
        ...groupData.value,
        alertEmails: deduplicated,
      }
      return true
    }
    const assets = ref([])
    const totalAssets = ref(0)
    const currentPage = ref(1)
    const perPage = ref(10)
    const asset = ref({ name: '', id: '' })
    const showModal = ref(false)
    const isLoadingAlarms = ref(false)

    // Tooltips
    const titleTootipMove = computed(() => root.$t('Notifica quando qualquer veículo do grupo inicia deslocamento'))
    const titleTootipWeekendMove = computed(() => root.$t('Notifica movimentação ocorrida exclusivamente em finais de semana'))
    const titleTootipIgnition = computed(() => root.$t('Notifica quando a ignição de um veículo for ligada'))
    const titleTootipOverSpeed = computed(() => root.$t('Notifica quando o veículo ultrapassa a velocidade máxima permitida'))
    const titleTootipHighFuelConsumption = computed(() => root.$t('Notifica quando o consumo de combustível excede o limite estabelecido'))
    const titleTootipLowFuel = computed(() => root.$t('Notifica quando o nível do tanque cai abaixo do limite configurado'))
    const titleTootipLongTime = computed(() => root.$t('Notifica quando o ativo fica sem enviar sinal por tempo prolongado'))
    const titleTootipTemperature = computed(() => root.$t('Notifica quando a temperatura atinge ou supera o limite seguro'))

    const alarmMove = ref({ id: '', val: false })
    const alarmWeekendMove = ref({ id: '', val: false })
    const alarmIgnition = ref({ id: '', val: false })
    const alarmLongTime = ref({ id: '', val: false, longTime: 0 })
    const alarmOverSpeed = ref({ id: '', val: false, speed: 0 })
    const alarmHighFuelConsumption = ref({ id: '', val: false, consumption: 0 })
    const alarmLowFuel = ref({ id: '', val: false, fuel: 0 })
    const alarmEngineCoolantTemperature = ref({ id: '', val: false, temperature: 0 })
    const alarmTransmissionTemperature = ref({ id: '', val: false, temperature: 0 })
    const alarmHydraulicTemperature = ref({ id: '', val: false, temperature: 0 })

    const maxSpeed = ref('')
    const fuel = ref('')
    const consumption = ref('')
    const longTime = ref('')
    const coolantTemperature = ref('')
    const hydraulicTemperature = ref('')
    const transmissionTemperature = ref('')

    const inputProps = computed(() => ({
      id: 'autosuggest__input',
      class: 'form-control',
      placeholder: root.$t('Digite ao menos 3 caracteres da placa ou chassi...'),
    }))

    const msgDialog = (msg, icon) => {
      root.$swal({
        position: 'top-end',
        icon: icon,
        title: msg,
        showConfirmButton: false,
        timer: 3000,
        customClass: { confirmButton: 'btn btn-primary' },
        buttonsStyling: false
      })
    }

    const getRef = (name) => {
      if (name === 'simpleRules' && simpleRules.value) return simpleRules.value
      if (context.refs && context.refs[name]) return context.refs[name]
      if (root && root.$refs && root.$refs[name]) return root.$refs[name]
      return undefined
    }

    // Modal Helpers
    const showModalLongTime = () => {
      longTime.value = alarmLongTime.value.longTime || ''
      const modal = getRef('modal-create-long-time')
      if (modal && modal.show) modal.show()
    }
    const hideModalLongTime = () => { const modal = getRef('modal-create-long-time'); if (modal && modal.hide) modal.hide() }
    const cancelLongTime = () => {
      if (!alarmLongTime.value.id) {
        alarmLongTime.value.val = false
      }
      hideModalLongTime()
    }
    const validateLongTime = () => {
      if (longTime.value && Number(longTime.value) > 0) {
        alarmLongTime.value.longTime = longTime.value
        createAlarmLongTime()
        hideModalLongTime()
      } else {
        msgDialog(root.$t('Informe uma quantidade válida de horas'), 'error')
      }
    }

    // Over Speed
    const showModalOverSpeed = () => {
      maxSpeed.value = alarmOverSpeed.value.speed || ''
      const modal = getRef('modal-create-over-speed')
      if (modal && modal.show) modal.show()
    }
    const hideModalOverSpeed = () => { const modal = getRef('modal-create-over-speed'); if (modal && modal.hide) modal.hide() }
    const cancelOverSpeed = () => {
      if (!alarmOverSpeed.value.id) {
        alarmOverSpeed.value.val = false
      }
      hideModalOverSpeed()
    }
    const validateOverSpeed = () => {
      if (maxSpeed.value && Number(maxSpeed.value) > 0) {
        alarmOverSpeed.value.speed = maxSpeed.value
        createAlarmOverSpeed()
        hideModalOverSpeed()
      } else {
        msgDialog(root.$t('Informe uma velocidade válida'), 'error')
      }
    }

    // High Fuel
    const showModalHighFuelConsumption = () => {
      consumption.value = alarmHighFuelConsumption.value.consumption || ''
      const modal = getRef('modal-create-high-fuel-consumption')
      if (modal && modal.show) modal.show()
    }
    const hideModalHighFuelConsumption = () => { const modal = getRef('modal-create-high-fuel-consumption'); if (modal && modal.hide) modal.hide() }
    const cancelHighFuelConsumption = () => {
      if (!alarmHighFuelConsumption.value.id) {
        alarmHighFuelConsumption.value.val = false
      }
      hideModalHighFuelConsumption()
    }
    const validateHighFuelConsumption = () => {
      if (consumption.value && Number(consumption.value) > 0) {
        alarmHighFuelConsumption.value.consumption = consumption.value
        createAlarmHighConsumption()
        hideModalHighFuelConsumption()
      } else {
        msgDialog(root.$t('Informe um consumo válido'), 'error')
      }
    }

    // Low Fuel
    const showModalLowFuel = () => {
      fuel.value = alarmLowFuel.value.fuel || ''
      const modal = getRef('modal-create-low-fuel')
      if (modal && modal.show) modal.show()
    }
    const hideModalLowFuel = () => { const modal = getRef('modal-create-low-fuel'); if (modal && modal.hide) modal.hide() }
    const cancelLowFuel = () => {
      if (!alarmLowFuel.value.id) {
        alarmLowFuel.value.val = false
      }
      hideModalLowFuel()
    }
    const validateLowFuel = () => {
      if (fuel.value && Number(fuel.value) > 0) {
        alarmLowFuel.value.fuel = fuel.value
        createAlarmLowFuel()
        hideModalLowFuel()
      } else {
        msgDialog(root.$t('Informe uma porcentagem válida'), 'error')
      }
    }

    // Engine Coolant
    const showModalEngineCoolantTemperature = () => {
      coolantTemperature.value = alarmEngineCoolantTemperature.value.temperature || ''
      const modal = getRef('modal-create-engine-coolant-temperature')
      if (modal && modal.show) modal.show()
    }
    const hideModalEngineCoolantTemperature = () => { const modal = getRef('modal-create-engine-coolant-temperature'); if (modal && modal.hide) modal.hide() }
    const cancelEngineCoolantTemperature = () => {
      if (!alarmEngineCoolantTemperature.value.id) {
        alarmEngineCoolantTemperature.value.val = false
      }
      hideModalEngineCoolantTemperature()
    }
    const validateEngineCoolantTemperature = () => {
      if (coolantTemperature.value && Number(coolantTemperature.value) > 0) {
        alarmEngineCoolantTemperature.value.temperature = coolantTemperature.value
        createAlarmEngineCoolantTemperature()
        hideModalEngineCoolantTemperature()
      } else {
        msgDialog(root.$t('Informe uma temperatura válida'), 'error')
      }
    }

    // Hydraulic
    const showModalHydraulicOilTemperature = () => {
      hydraulicTemperature.value = alarmHydraulicTemperature.value.temperature || ''
      const modal = getRef('modal-create-hydraulic-oil-temperature')
      if (modal && modal.show) modal.show()
    }
    const hideModalHydraulicOilTemperature = () => { const modal = getRef('modal-create-hydraulic-oil-temperature'); if (modal && modal.hide) modal.hide() }
    const cancelHydraulicOilTemperature = () => {
      if (!alarmHydraulicTemperature.value.id) {
        alarmHydraulicTemperature.value.val = false
      }
      hideModalHydraulicOilTemperature()
    }
    const validateHydraulicOilTemperature = () => {
      if (hydraulicTemperature.value && Number(hydraulicTemperature.value) > 0) {
        alarmHydraulicTemperature.value.temperature = hydraulicTemperature.value
        createAlarmHydraulicOilTemperature()
        hideModalHydraulicOilTemperature()
      } else {
        msgDialog(root.$t('Informe uma temperatura válida'), 'error')
      }
    }

    // Transmission
    const showModalTransmissionOilTemperature = () => {
      transmissionTemperature.value = alarmTransmissionTemperature.value.temperature || ''
      const modal = getRef('modal-create-transmission-oil-temperature')
      if (modal && modal.show) modal.show()
    }
    const hideModalTransmissionOilTemperature = () => { const modal = getRef('modal-create-transmission-oil-temperature'); if (modal && modal.hide) modal.hide() }
    const cancelTransmissionOilTemperature = () => {
      if (!alarmTransmissionTemperature.value.id) {
        alarmTransmissionTemperature.value.val = false
      }
      hideModalTransmissionOilTemperature()
    }
    const validateTransmissionOilTemperature = () => {
      if (transmissionTemperature.value && Number(transmissionTemperature.value) > 0) {
        alarmTransmissionTemperature.value.temperature = transmissionTemperature.value
        createAlarmTransmissionOilTemperature()
        hideModalTransmissionOilTemperature()
      } else {
        msgDialog(root.$t('Informe uma temperatura válida'), 'error')
      }
    }

    // Alarm API Calls
    const handleAlarmCreation = (action, payload, alarmRef) => {
      const executeCreate = () => {
        store.dispatch(action, payload)
          .then(() => {
            msgDialog(root.$t('Alarme ativado com sucesso!'), 'success')
            fetchAlarms()
          })
          .catch(() => {
            alarmRef.value.val = !alarmRef.value.val
            fetchAlarms()
            msgDialog(root.$t('Erro ao tentar salvar o alarme.'), 'error')
          })
      }

      // If alarm was already registered and has an ID, delete previous first to avoid duplicate handler rejection
      if (alarmRef.value.id) {
        store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/deleteAlarm`, { id: alarmRef.value.id })
          .then(() => executeCreate())
          .catch(() => executeCreate())
      } else {
        executeCreate()
      }
    }

    const createAlarmLongTime = () => handleAlarmCreation(`${ALARMS_APP_STORE_MODULE_NAME}/addLongTimeAlarm`, { assetGroupId: groupId, value: alarmLongTime.value.longTime }, alarmLongTime)
    const createAlarmMove = () => handleAlarmCreation(`${ALARMS_APP_STORE_MODULE_NAME}/addMoveAlarm`, { assetGroupId: groupId }, alarmMove)
    const createAlarmWeekendMove = () => handleAlarmCreation(`${ALARMS_APP_STORE_MODULE_NAME}/addAlarmWeekendMove`, { assetGroupId: groupId }, alarmWeekendMove)
    const createAlarmIgnition = () => handleAlarmCreation(`${ALARMS_APP_STORE_MODULE_NAME}/addIgnitionAlarm`, { assetGroupId: groupId }, alarmIgnition)
    const createAlarmOverSpeed = () => handleAlarmCreation(`${ALARMS_APP_STORE_MODULE_NAME}/addOverSpeedAlarm`, { assetGroupId: groupId, value: alarmOverSpeed.value.speed }, alarmOverSpeed)
    const createAlarmHighConsumption = () => handleAlarmCreation(`${ALARMS_APP_STORE_MODULE_NAME}/addHighConsumptionAlarm`, { assetGroupId: groupId, value: alarmHighFuelConsumption.value.consumption }, alarmHighFuelConsumption)
    const createAlarmLowFuel = () => handleAlarmCreation(`${ALARMS_APP_STORE_MODULE_NAME}/addLowFuelAlarm`, { assetGroupId: groupId, value: alarmLowFuel.value.fuel }, alarmLowFuel)
    const createAlarmEngineCoolantTemperature = () => handleAlarmCreation(`${ALARMS_APP_STORE_MODULE_NAME}/addEngineCoolantTemperatureAlarm`, { assetGroupId: groupId, value: alarmEngineCoolantTemperature.value.temperature }, alarmEngineCoolantTemperature)
    const createAlarmHydraulicOilTemperature = () => handleAlarmCreation(`${ALARMS_APP_STORE_MODULE_NAME}/addHydraulicOilTemperatureAlarm`, { assetGroupId: groupId, value: alarmHydraulicTemperature.value.temperature }, alarmHydraulicTemperature)
    const createAlarmTransmissionOilTemperature = () => handleAlarmCreation(`${ALARMS_APP_STORE_MODULE_NAME}/addTransmissionOilTemperatureAlarm`, { assetGroupId: groupId, value: alarmTransmissionTemperature.value.temperature }, alarmTransmissionTemperature)

    const MOVE_ALARM_NAME = "Move Alarm"
    const WEEKEND_MOVE_ALARM_NAME = "Weekend Move Alarm"
    const IGNITION_ALARM_NAME = "Ignition Alarm"
    const OVER_SPEED_ALARM_NAME = "Overspeed Alarm"
    const HIGH_FUEL_CONSUMPTION_ALARM_NAME = "High Fuel Consumption Alarm"
    const LOW_FUEL_ALARM_NAME = "Low Fuel Alarm"
    const LONG_TIME_ALARM_NAME = "Long time without connection alarm"
    const ENGINE_COOLANT_TEMPERATURE_ALARM_NAME = 'Alarme de temperatura do motor'
    const HYDRAULIC_OIL_TEMPERATURE_ALARM_NAME = 'Alarme de temperatura do óleo hidráulico'
    const TRANSMISSION_OIL_TEMPERATURE_ALARM_NAME = 'Alarme de temperatura da transmissão'

    const deleteAlarm = (id, typeAlarm) => {
      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/deleteAlarm`, { id })
        .then(() => {
          const resetMap = {
            [OVER_SPEED_ALARM_NAME]: () => { alarmOverSpeed.value.val = false; alarmOverSpeed.value.speed = 0; maxSpeed.value = ''; alarmOverSpeed.value.id = '' },
            [HIGH_FUEL_CONSUMPTION_ALARM_NAME]: () => { alarmHighFuelConsumption.value.val = false; alarmHighFuelConsumption.value.consumption = 0; consumption.value = ''; alarmHighFuelConsumption.value.id = '' },
            [LOW_FUEL_ALARM_NAME]: () => { alarmLowFuel.value.val = false; alarmLowFuel.value.fuel = 0; fuel.value = ''; alarmLowFuel.value.id = '' },
            [LONG_TIME_ALARM_NAME]: () => { alarmLongTime.value.val = false; alarmLongTime.value.longTime = 0; longTime.value = ''; alarmLongTime.value.id = '' },
            [ENGINE_COOLANT_TEMPERATURE_ALARM_NAME]: () => { alarmEngineCoolantTemperature.value.val = false; alarmEngineCoolantTemperature.value.temperature = 0; coolantTemperature.value = ''; alarmEngineCoolantTemperature.value.id = '' },
            [HYDRAULIC_OIL_TEMPERATURE_ALARM_NAME]: () => { alarmHydraulicTemperature.value.val = false; alarmHydraulicTemperature.value.temperature = 0; hydraulicTemperature.value = ''; alarmHydraulicTemperature.value.id = '' },
            [TRANSMISSION_OIL_TEMPERATURE_ALARM_NAME]: () => { alarmTransmissionTemperature.value.val = false; alarmTransmissionTemperature.value.temperature = 0; transmissionTemperature.value = ''; alarmTransmissionTemperature.value.id = '' },
            [MOVE_ALARM_NAME]: () => { alarmMove.value.val = false; alarmMove.value.id = '' },
            [WEEKEND_MOVE_ALARM_NAME]: () => { alarmWeekendMove.value.val = false; alarmWeekendMove.value.id = '' },
            [IGNITION_ALARM_NAME]: () => { alarmIgnition.value.val = false; alarmIgnition.value.id = '' },
          }
          if (resetMap[typeAlarm]) resetMap[typeAlarm]()
          msgDialog(root.$t('Alarme desativado!'), 'warning')
        })
        .catch(() => {
          fetchAlarms()
          root.$swal(root.$t('Erro'), root.$t('Não foi possível desativar o alarme.'), 'error')
        })
    }

    const toogleAlarm = (typeAlarm) => {
      const toggleMap = {
        [MOVE_ALARM_NAME]: () => alarmMove.value.val ? createAlarmMove() : deleteAlarm(alarmMove.value.id, MOVE_ALARM_NAME),
        [WEEKEND_MOVE_ALARM_NAME]: () => alarmWeekendMove.value.val ? createAlarmWeekendMove() : deleteAlarm(alarmWeekendMove.value.id, WEEKEND_MOVE_ALARM_NAME),
        [IGNITION_ALARM_NAME]: () => alarmIgnition.value.val ? createAlarmIgnition() : deleteAlarm(alarmIgnition.value.id, IGNITION_ALARM_NAME),
        [LONG_TIME_ALARM_NAME]: () => alarmLongTime.value.val ? showModalLongTime() : deleteAlarm(alarmLongTime.value.id, LONG_TIME_ALARM_NAME),
        [OVER_SPEED_ALARM_NAME]: () => alarmOverSpeed.value.val ? showModalOverSpeed() : deleteAlarm(alarmOverSpeed.value.id, OVER_SPEED_ALARM_NAME),
        [HIGH_FUEL_CONSUMPTION_ALARM_NAME]: () => alarmHighFuelConsumption.value.val ? showModalHighFuelConsumption() : deleteAlarm(alarmHighFuelConsumption.value.id, HIGH_FUEL_CONSUMPTION_ALARM_NAME),
        [LOW_FUEL_ALARM_NAME]: () => alarmLowFuel.value.val ? showModalLowFuel() : deleteAlarm(alarmLowFuel.value.id, LOW_FUEL_ALARM_NAME),
        [ENGINE_COOLANT_TEMPERATURE_ALARM_NAME]: () => alarmEngineCoolantTemperature.value.val ? showModalEngineCoolantTemperature() : deleteAlarm(alarmEngineCoolantTemperature.value.id, ENGINE_COOLANT_TEMPERATURE_ALARM_NAME),
        [HYDRAULIC_OIL_TEMPERATURE_ALARM_NAME]: () => alarmHydraulicTemperature.value.val ? showModalHydraulicOilTemperature() : deleteAlarm(alarmHydraulicTemperature.value.id, HYDRAULIC_OIL_TEMPERATURE_ALARM_NAME),
        [TRANSMISSION_OIL_TEMPERATURE_ALARM_NAME]: () => alarmTransmissionTemperature.value.val ? showModalTransmissionOilTemperature() : deleteAlarm(alarmTransmissionTemperature.value.id, TRANSMISSION_OIL_TEMPERATURE_ALARM_NAME),
      }
      if (toggleMap[typeAlarm]) {
        toggleMap[typeAlarm]()
      } else {
        msgDialog(root.$t('Falha na operação'), 'error')
      }
    }

    const fetchAlarms = () => {
      if (!groupId) return
      isLoadingAlarms.value = true
      store.dispatch(`${ALARMS_APP_STORE_MODULE_NAME}/fetchAlarms`, { assetGroupId: groupId })
        .then((res) => {
          const fetchedAlarms = res.data.data
          // Reset local flags first
          alarmMove.value = { id: '', val: false }
          alarmWeekendMove.value = { id: '', val: false }
          alarmIgnition.value = { id: '', val: false }
          alarmLongTime.value = { id: '', val: false, longTime: 0 }
          alarmOverSpeed.value = { id: '', val: false, speed: 0 }
          alarmHighFuelConsumption.value = { id: '', val: false, consumption: 0 }
          alarmLowFuel.value = { id: '', val: false, fuel: 0 }
          alarmEngineCoolantTemperature.value = { id: '', val: false, temperature: 0 }
          alarmTransmissionTemperature.value = { id: '', val: false, temperature: 0 }
          alarmHydraulicTemperature.value = { id: '', val: false, temperature: 0 }

          if (fetchedAlarms && fetchedAlarms.length > 0) {
            const alarmMap = {
              [MOVE_ALARM_NAME]: { alarm: alarmMove },
              [WEEKEND_MOVE_ALARM_NAME]: { alarm: alarmWeekendMove },
              [IGNITION_ALARM_NAME]: { alarm: alarmIgnition },
              [OVER_SPEED_ALARM_NAME]: { alarm: alarmOverSpeed, extraProp: "speed" },
              [HIGH_FUEL_CONSUMPTION_ALARM_NAME]: { alarm: alarmHighFuelConsumption, extraProp: "consumption" },
              [LOW_FUEL_ALARM_NAME]: { alarm: alarmLowFuel, extraProp: "fuel" },
              [LONG_TIME_ALARM_NAME]: { alarm: alarmLongTime, extraProp: "longTime" },
              [ENGINE_COOLANT_TEMPERATURE_ALARM_NAME]: { alarm: alarmEngineCoolantTemperature, extraProp: "temperature" },
              [HYDRAULIC_OIL_TEMPERATURE_ALARM_NAME]: { alarm: alarmHydraulicTemperature, extraProp: "temperature" },
              [TRANSMISSION_OIL_TEMPERATURE_ALARM_NAME]: { alarm: alarmTransmissionTemperature, extraProp: "temperature" },
            }
            fetchedAlarms.forEach((alarm) => {
              const config = alarmMap[alarm.name]
              if (config) {
                config.alarm.value.val = true
                config.alarm.value.id = alarm.id
                if (config.extraProp && alarm.value !== null && alarm.value !== undefined) {
                  config.alarm.value[config.extraProp] = alarm.value
                }
              }
            })
          }
        })
        .catch(() => {})
        .finally(() => {
          isLoadingAlarms.value = false
        })
    }

    const getSuggestionValue = (option) => {
      if (!option) return ''
      if (option.item) {
        return option.item.plate || option.item.chassisNumber || ''
      }
      return typeof option === 'string' ? option : ''
    }

    const selectHandler = (option) => {
      if (!option || !option.item) return
      asset.value.id = option.item.id
      asset.value.name = option.item.plate ? option.item.plate : option.item.chassisNumber
      filteredOptions.value = []
    }

    const onInputChange = (text) => {
      if (text === null || text === undefined) return
      if (typeof text === 'object' || text === '[object Object]') {
        asset.value.name = ''
        asset.value.id = ''
        filteredOptions.value = []
        return
      }

      if (asset.value.id && text !== asset.value.name) {
        asset.value.id = ''
      }

      if (text.length < 3) {
        filteredOptions.value = []
        return
      }
      store.dispatch('app-management/fetchAssets', { q: text, excludeGrouped: true })
        .then(response => {
          filteredOptions.value = [{ data: response.data.data }]
        })
        .catch(() => {})
    }

    const openModalBind = () => { showModal.value = true }
    const limparFormCadastro = () => {
      asset.value = { name: '', id: '' }
      filteredOptions.value = []
      isBindingAsset.value = false
    }

    const fetchAssetsOfAssetGroup = () => {
      if (!groupId) return
      store.dispatch('app-management/fetchAssetsOfAssetGroup', { groupId: groupId, perPage: perPage.value, page: currentPage.value })
        .then(response => {
          assets.value = response.data.data || []
          totalAssets.value = response.data.totalItems || 0
        })
        .catch(() => {})
    }

    const bindGroup = () => {
      isBindingAsset.value = true
      store.dispatch(`${MANAGEMENT_APP_STORE_MODULE_NAME}/bindAssetToAssetGroup`, { assetId: asset.value.id, assetGroupId: groupId })
        .then(() => {
          msgDialog(root.$t('Ativo vinculado com sucesso!'), 'success')
          showModal.value = false
          fetchAssetsOfAssetGroup()
        })
        .catch(() => {
          root.$swal(root.$t('Erro'), root.$t('Não foi possível vincular o ativo.'), 'error')
        })
        .finally(() => {
          isBindingAsset.value = false
        })
    }

    const unBindAssetToAssetGroup = (assetId, bindId) => {
      store.dispatch('app-management/unBindAssetToAssetGroup', { assetId: assetId, bindId: bindId, assetGroupId: groupId })
        .then(() => {
          msgDialog(root.$t('Ativo desvinculado com sucesso!'), 'success')
          fetchAssetsOfAssetGroup()
        })
        .catch(() => {
          root.$swal(root.$t('Erro'), root.$t('Não foi possível desvincular o ativo.'), 'error')
        })
    }

    const modalDelete = (assetId, bindId) => {
      root.$swal({
        title: root.$t('Desvincular Ativo?'),
        text: root.$t('O ativo será removido deste grupo e deixará de receber as regras de alarme configuradas aqui.'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: root.$t('Sim, desvincular'),
        cancelButtonText: root.$t('Cancelar'),
        customClass: {
          confirmButton: 'btn btn-danger mr-1',
          cancelButton: 'btn btn-outline-secondary'
        },
        buttonsStyling: false
      }).then((result) => {
        if (result.value) {
          unBindAssetToAssetGroup(assetId, bindId)
        }
      })
    }

    const fetchGroup = () => {
      if (!groupId) return
      store.dispatch('app-management/fetchGroup', { id: groupId })
        .then(response => {
          const rawData = (response && response.data && response.data.data) ? response.data.data : (response ? response.data : {})
          groupData.value = {
            ...rawData,
            id: rawData.id || groupId,
            name: rawData.name || '',
            description: rawData.description || '',
            alertEmails: sanitizeAndDeduplicateEmails(rawData.alertEmails || []),
          }
        })
        .catch(() => {})
    }

    const saveGroup = () => {
      isSavingGroup.value = true
      if (!groupData.value.id) {
        store.dispatch('app-management/addGroup', { groupData: groupData.value })
          .then(() => {
            msgDialog(root.$t('Grupo cadastrado com sucesso!'), 'success')
            router.push({ name: 'list-group' })
          })
          .catch(() => {
            msgDialog(root.$t('Erro ao salvar o grupo.'), 'error')
          })
          .finally(() => {
            isSavingGroup.value = false
          })
      } else {
        if (!validateAlertEmailsBeforeSave()) {
          isSavingGroup.value = false
          msgDialog(alertEmailError.value, 'error')
          return
        }
        const updatePayload = {
          id: groupData.value.id,
          name: groupData.value.name ? groupData.value.name.trim() : '',
          description: groupData.value.description || '',
          alertEmails: sanitizeAndDeduplicateEmails(groupData.value.alertEmails),
        }
        store.dispatch('app-management/updateassetgroup', updatePayload)
          .then(() => {
            msgDialog(root.$t('Grupo atualizado com sucesso!'), 'success')
            router.push({ name: 'list-group' })
          })
          .catch(() => {
            msgDialog(root.$t('Erro ao atualizar o grupo.'), 'error')
          })
          .finally(() => {
            isSavingGroup.value = false
          })
      }
    }

    const validationForm = () => {
      const observer = getRef('simpleRules')
      if (observer && typeof observer.validate === 'function') {
        observer.validate().then(success => {
          if (success) {
            saveGroup()
          }
        })
      } else {
        saveGroup()
      }
    }

    watch(currentPage, () => {
      if (groupId) fetchAssetsOfAssetGroup()
    })

    onMounted(() => {
      if (groupId) {
        fetchGroup()
        fetchAssetsOfAssetGroup()
      }
      fetchAlarms()
    })

    return {
      required,
      groupId,
      MANAGEMENT_APP_STORE_MODULE_NAME,
      ALARMS_APP_STORE_MODULE_NAME,
      MOVE_ALARM_NAME,
      WEEKEND_MOVE_ALARM_NAME,
      IGNITION_ALARM_NAME,
      OVER_SPEED_ALARM_NAME,
      LOW_FUEL_ALARM_NAME,
      HIGH_FUEL_CONSUMPTION_ALARM_NAME,
      LONG_TIME_ALARM_NAME,
      ENGINE_COOLANT_TEMPERATURE_ALARM_NAME,
      HYDRAULIC_OIL_TEMPERATURE_ALARM_NAME,
      TRANSMISSION_OIL_TEMPERATURE_ALARM_NAME,

      showCreationBanner,
      filteredOptions,
      isSavingGroup,
      isBindingAsset,
      groupData,
      assets,
      totalAssets,
      currentPage,
      perPage,
      asset,
      showModal,
      isLoadingAlarms,

      titleTootipMove,
      titleTootipWeekendMove,
      titleTootipIgnition,
      titleTootipOverSpeed,
      titleTootipHighFuelConsumption,
      titleTootipLowFuel,
      titleTootipLongTime,
      titleTootipTemperature,

      alarmMove,
      alarmWeekendMove,
      alarmIgnition,
      alarmLongTime,
      alarmOverSpeed,
      alarmHighFuelConsumption,
      alarmLowFuel,
      alarmEngineCoolantTemperature,
      alarmTransmissionTemperature,
      alarmHydraulicTemperature,

      maxSpeed,
      fuel,
      consumption,
      longTime,
      coolantTemperature,
      hydraulicTemperature,
      transmissionTemperature,

      inputProps,
      msgDialog,

      showModalLongTime,
      cancelLongTime,
      validateLongTime,

      showModalOverSpeed,
      cancelOverSpeed,
      validateOverSpeed,

      showModalHighFuelConsumption,
      cancelHighFuelConsumption,
      validateHighFuelConsumption,

      showModalLowFuel,
      cancelLowFuel,
      validateLowFuel,

      showModalEngineCoolantTemperature,
      cancelEngineCoolantTemperature,
      validateEngineCoolantTemperature,

      showModalHydraulicOilTemperature,
      cancelHydraulicOilTemperature,
      validateHydraulicOilTemperature,

      showModalTransmissionOilTemperature,
      cancelTransmissionOilTemperature,
      validateTransmissionOilTemperature,

      toogleAlarm,
      fetchAlarms,
      getSuggestionValue,
      selectHandler,
      onInputChange,
      openModalBind,
      limparFormCadastro,
      bindGroup,
      modalDelete,
      validationForm,
      simpleRules,
      MAX_ALERT_EMAILS,
      MAX_ALERT_EMAIL_LENGTH,
      alertEmailInput,
      alertEmailError,
      alertEmailDuplicateNotice,
      alertEmailsList,
      isAlertEmailLimitReached,
      isValidAlertEmail,
      sanitizeAndDeduplicateEmails,
      onAlertEmailInput,
      addAlertEmail,
      removeAlertEmail,
      clearAlertEmails,
      onAlertEmailKeydown,
      onAlertEmailPaste,
      validateAlertEmailsBeforeSave,
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.register-group-wrapper {
  .asset-plate-badge {
    background: #f1f1f4;
    color: #4b4b5a;
    padding: 3px 8px;
    border-radius: 4px;
    letter-spacing: 0.5px;
    border: 1px solid #e2e2e8;
    display: inline-block;
  }

  .alert-emails-section {
    border: 1px solid #ebe9f1;
    border-radius: 0.5rem;
    padding: 0.85rem 1rem;
    background-color: #fafafc;
    transition: border-color 0.2s ease-in-out;

    &:focus-within {
      border-color: rgba(115, 103, 240, 0.45);
      background-color: #ffffff;
      box-shadow: 0 2px 8px rgba(115, 103, 240, 0.06);
    }
  }

  .alert-emails-rule-banner {
    background-color: rgba(115, 103, 240, 0.06);
    border-left: 3px solid #7367f0;
    border-radius: 0.35rem;
    padding: 0.45rem 0.7rem;
  }

  .alert-emails-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .alert-email-chip {
    display: inline-flex;
    align-items: center;
    background-color: rgba(115, 103, 240, 0.08);
    border: 1px solid rgba(115, 103, 240, 0.25);
    color: #5e5873;
    border-radius: 50rem;
    padding: 0.28rem 0.4rem 0.28rem 0.7rem;
    font-size: 0.82rem;
    font-weight: 600;
    max-width: 100%;
    transition: all 0.15s ease-in-out;

    &:hover {
      background-color: rgba(115, 103, 240, 0.14);
      border-color: rgba(115, 103, 240, 0.45);
    }

    .alert-email-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 280px;
    }

    .alert-email-remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 0;
      background: transparent;
      color: #6e6b7b;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      margin-left: 0.35rem;
      padding: 0;
      cursor: pointer;
      transition: all 0.15s ease-in-out;

      &:hover {
        background-color: rgba(234, 84, 85, 0.15);
        color: #ea5455;
      }
    }
  }

  .alert-emails-empty {
    border: 1px dashed #d8d6de;
    border-radius: 0.35rem;
    padding: 0.55rem 0.75rem;
    background-color: rgba(255, 255, 255, 0.6);
  }

  .alarms-grid {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }

  .alarm-item-card {
    border: 1px solid #ebe9f1;
    border-radius: 0.5rem;
    padding: 0.75rem 0.85rem;
    background-color: #ffffff;
    transition: all 0.2s ease-in-out;

    &:hover {
      background-color: #fafafc;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.04);
      transform: translateY(-1px);
    }

    &.is-active {
      border-color: #7367f0 !important;
      border-left-width: 4px;
      background-color: rgba(115, 103, 240, 0.03);
    }
  }
}

.dark-layout {
  .register-group-wrapper {
    .asset-plate-badge {
      background: $theme-dark-body-bg;
      color: $theme-dark-body-color;
      border-color: $theme-dark-border-color;
    }

    .alert-emails-section {
      background-color: darken($theme-dark-card-bg, 2%);
      border-color: $theme-dark-border-color;

      &:focus-within {
        background-color: $theme-dark-card-bg;
        border-color: #7367f0;
      }
    }

    .alert-emails-rule-banner {
      background-color: rgba(115, 103, 240, 0.12);
    }

    .alert-email-chip {
      background-color: rgba(115, 103, 240, 0.16);
      border-color: rgba(115, 103, 240, 0.35);
      color: $theme-dark-body-color;

      .alert-email-remove {
        color: $theme-dark-body-color;
      }
    }

    .alert-emails-empty {
      border-color: $theme-dark-border-color;
      background-color: rgba(255, 255, 255, 0.02);
    }

    .alarm-item-card {
      background-color: $theme-dark-card-bg;
      border-color: $theme-dark-border-color;

      &:hover {
        background-color: lighten($theme-dark-card-bg, 3%);
      }

      &.is-active {
        background-color: rgba(115, 103, 240, 0.08);
        border-color: #7367f0 !important;
      }
    }
  }
}
</style>
